import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { PromoLabel } from '@/components/LabelsSection';
import { usePromotionsAvailability } from '@/modules/promotions';
import { useMissions } from '@/modules/promotions/mission/hooks/useMissions';
import { useGetCommonStore } from '@/store/commonStore';

export const useLabels = (isDashboard?: boolean): PromoLabel[] => {
  const useCommonStore = useGetCommonStore();

  const {
    user,
    userBonanzaGroupKey,
    diceRollMenuData,
    bonanzaPromoMenuData,
    scratchGameMenuData,
    fortuneWheelMenuData,
  } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      userBonanzaGroupKey: state.userBonanzaGroupKey,
      diceRollMenuData: state.diceRollMenuData,
      bonanzaPromoMenuData: state.bonanzaPromoMenuData,
      scratchGameMenuData: state.scratchGameMenuData,
      fortuneWheelMenuData: state.fortuneWheelMenuData,
    }))
  );

  const {
    isQuestAvailable,
    isScratchGameAvailable,
    isBonanzaPromoAvailable,
    isFortuneWheelAvailable,
    isDiceRollAvailable,
    isMissionAvailable,
  } = usePromotionsAvailability();

  const { currentMission } = useMissions();

  const labels = useMemo(
    () => [
      {
        character: bonanzaPromoMenuData?.menu?.charactersByStatus?.find(
          ({ promoStatus }) => promoStatus === userBonanzaGroupKey
        )?.image?.url,
        background: bonanzaPromoMenuData?.menu?.bg?.url,
        url: '/bonanza',
        withBorder: true,
        available:
          !user && !!bonanzaPromoMenuData?.menu && isBonanzaPromoAvailable,
      },
      {
        // character: scratchGameMenuData?.menu?.character?.url,
        background: scratchGameMenuData?.menu?.bg?.url,
        url: '/scratch-card',
        available:
          !isDashboard && !!scratchGameMenuData?.menu && isScratchGameAvailable,
      },
      {
        character: fortuneWheelMenuData?.menu?.character?.url,
        background: fortuneWheelMenuData?.menu?.bg?.url,
        url: '/lucky-wheel',
        available:
          !isDashboard &&
          !!fortuneWheelMenuData?.menu &&
          isFortuneWheelAvailable,
      },
      {
        character: diceRollMenuData?.menu?.character?.url,
        background: diceRollMenuData?.menu?.bg?.url,
        url: '/dice-roll',
        available:
          !isDashboard && !!diceRollMenuData?.menu && isDiceRollAvailable,
      },
      {
        character: '/quest-xmas/menu-character.webp',
        background: '/quest-xmas/menu-bg.webp',
        url: '/quest',
        available: !isDashboard && isQuestAvailable,
      },
      {
        character: currentMission?.imgSrc,
        background: '/mission/menu-bg.webp',
        url: '/mission',
        available: !isDashboard && isMissionAvailable,
      },
    ],
    [
      user,
      isDashboard,
      userBonanzaGroupKey,
      bonanzaPromoMenuData,
      isQuestAvailable,
      isScratchGameAvailable,
      scratchGameMenuData,
      isBonanzaPromoAvailable,
      fortuneWheelMenuData,
      isFortuneWheelAvailable,
      isDiceRollAvailable,
      diceRollMenuData,
      isMissionAvailable,
      currentMission,
    ]
  );

  return useMemo(() => labels.filter((label) => label.available), [labels]);
};
